import {
  isSkillTextFile,
  MAX_SKILL_FILE_BYTES,
  MAX_SKILL_REFERENCE_BYTES,
  MAX_SKILL_REFERENCES,
  MAX_SKILL_REFERENCES_TOTAL,
  parseSkillMarkdown,
  type SkillReference,
  type ImportSkillsResult,
  type SkillFile,
  type SkillImportItem,
} from '@agent-platform/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FolderOpen, FolderUp } from 'lucide-react';
import { type DragEvent, useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ErrorAlert } from '@/components/error-alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

/** A file picked or dropped, with its path relative to the chosen folder. */
interface PickedFile {
  path: string;
  file: File;
}

const IGNORED_DIR = /(^|\/)(node_modules|\.git|\.venv|__pycache__|dist|build)(\/|$)/;
const isSkillMd = (path: string) => /(^|\/)SKILL\.md$/i.test(path);
/** Request bodies stay under the import route's 4 MB limit (JSON escaping adds some). */
const BATCH_BYTES = 1_400_000;

/** Reads a skill folder's other files: text references are kept, the rest reported. */
async function readReferences(files: Array<{ rel: string; file: File }>) {
  const references: SkillReference[] = [];
  const skippedFiles: Array<{ path: string; reason: string }> = [];
  let total = 0;
  for (const { rel, file } of [...files].sort((a, b) => a.rel.localeCompare(b.rel))) {
    const skip = (reason: string) => skippedFiles.push({ path: rel, reason });
    if (/(^|\/)\.(DS_Store|gitignore|gitkeep)$/.test(rel)) continue;
    if (!isSkillTextFile(rel)) skip('not a text file');
    else if (file.size > MAX_SKILL_REFERENCE_BYTES)
      skip(`larger than ${MAX_SKILL_REFERENCE_BYTES / 1024} KB`);
    else if (references.length >= MAX_SKILL_REFERENCES) skip('too many files');
    else if (total + file.size > MAX_SKILL_REFERENCES_TOTAL)
      skip('the skill’s 1 MB limit is reached');
    else {
      const content = await file.text();
      if (content.includes('\0')) skip('binary content');
      else {
        references.push({ path: rel, content });
        total += content.length;
      }
    }
  }
  return { references, skippedFiles: skippedFiles.slice(0, 500) };
}

/** Reads a dropped folder tree (Chrome, Edge, Safari, Firefox: `webkitGetAsEntry`). */
async function readEntry(entry: FileSystemEntry, prefix: string, out: PickedFile[]): Promise<void> {
  const path = prefix ? `${prefix}/${entry.name}` : entry.name;
  if (IGNORED_DIR.test(path)) return;
  if (entry.isFile) {
    const file = await new Promise<File>((resolve, reject) =>
      (entry as FileSystemFileEntry).file(resolve, reject),
    );
    out.push({ path, file });
    return;
  }
  const reader = (entry as FileSystemDirectoryEntry).createReader();
  // readEntries returns results in chunks until it returns an empty list.
  for (;;) {
    const chunk = await new Promise<FileSystemEntry[]>((resolve, reject) =>
      reader.readEntries(resolve, reject),
    );
    if (chunk.length === 0) break;
    for (const child of chunk) await readEntry(child, path, out);
  }
}

interface Collected {
  /** SKILL.md files to send. */
  skills: SkillFile[];
  /** Problems found before sending (too large, duplicate names). */
  local: SkillImportItem[];
  folderName: string;
}

/** Groups picked files into skills: each SKILL.md plus the other files of its folder. */
async function collect(files: PickedFile[]): Promise<Collected> {
  const kept = files.filter((f) => !IGNORED_DIR.test(f.path));
  let skillFiles = kept.filter((f) => isSkillMd(f.path));
  // A loose markdown file (no SKILL.md anywhere) is treated as a single-file skill.
  if (skillFiles.length === 0) skillFiles = kept.filter((f) => /\.md$/i.test(f.path));
  const dirs = skillFiles.map((f) => f.path.split('/').slice(0, -1).join('/'));
  const skills: SkillFile[] = [];
  const local: SkillImportItem[] = [];
  const names = new Set<string>();
  for (const [i, picked] of skillFiles.entries()) {
    const dir = dirs[i]!;
    // Other files in this skill's folder, but not in a nested skill's folder.
    const otherFiles = dir
      ? kept
          .filter((f) => f !== picked && f.path.startsWith(`${dir}/`))
          .filter((f) => {
            const owner = dirs
              .filter((d) => f.path.startsWith(`${d}/`))
              .sort((a, b) => b.length - a.length)[0];
            return owner === dir;
          })
          .map((f) => ({ rel: f.path.slice(dir.length + 1), file: f.file }))
      : [];
    if (picked.file.size > MAX_SKILL_FILE_BYTES) {
      local.push({
        path: picked.path,
        name: picked.path.split('/').at(-2) ?? picked.path,
        description: '',
        size: 0,
        files: [],
        status: 'skip',
        reason: `SKILL.md is larger than ${MAX_SKILL_FILE_BYTES / 1024} KB`,
        warnings: [],
      });
      continue;
    }
    const content = await picked.file.text();
    const parsed = parseSkillMarkdown(picked.path, content);
    if (parsed.ok && names.has(parsed.name)) {
      local.push({
        path: picked.path,
        name: parsed.name,
        description: parsed.description,
        size: parsed.instructions.length,
        files: [],
        status: 'skip',
        reason: `Another SKILL.md in this folder is also named “${parsed.name}”`,
        warnings: [],
      });
      continue;
    }
    if (parsed.ok) names.add(parsed.name);
    skills.push({ path: picked.path, content, ...(await readReferences(otherFiles)) });
  }
  return { skills, local, folderName: files[0]?.path.split('/')[0] ?? '' };
}

function batches(skills: SkillFile[]): SkillFile[][] {
  const out: SkillFile[][] = [];
  let current: SkillFile[] = [];
  let bytes = 0;
  for (const skill of skills) {
    const size = JSON.stringify(skill).length;
    if (current.length > 0 && (bytes + size > BATCH_BYTES || current.length >= 100)) {
      out.push(current);
      current = [];
      bytes = 0;
    }
    current.push(skill);
    bytes += size;
  }
  if (current.length) out.push(current);
  return out;
}

const STATUS_LABEL: Record<SkillImportItem['status'], string> = {
  create: 'will be added',
  update: 'will be updated',
  created: 'added',
  updated: 'updated',
  skip: 'skipped',
};

/** Choose or drop a skills folder (e.g. ~/.claude/skills): every SKILL.md becomes a skill. */
export function SkillsFolderImportDialog({ tenantId }: { tenantId: string }) {
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [collected, setCollected] = useState<Collected | null>(null);
  const [onConflict, setOnConflict] = useState<'skip' | 'update'>('skip');
  const [result, setResult] = useState<ImportSkillsResult | null>(null);
  const [dragging, setDragging] = useState(false);
  const [reading, setReading] = useState(false);

  // `webkitdirectory` is not in React's input types: set it as soon as the input mounts (the
  // dialog content mounts after `open` changes, so an effect would run too early).
  const folderInput = useCallback((el: HTMLInputElement | null) => {
    inputRef.current = el;
    el?.setAttribute('webkitdirectory', '');
    el?.setAttribute('directory', '');
  }, []);

  const send = useMutation({
    mutationFn: async ({ dryRun }: { dryRun: boolean }) => {
      const items: SkillImportItem[] = [];
      for (const batch of batches(collected?.skills ?? [])) {
        const r = await api<ImportSkillsResult>('/skills/import', {
          method: 'POST',
          body: { skills: batch, onConflict, dryRun },
        });
        items.push(...r.items);
      }
      return { dryRun, items: [...items, ...(collected?.local ?? [])] };
    },
    onSuccess: (r) => {
      setResult(r);
      if (r.dryRun) return;
      const added = r.items.filter((i) => i.status === 'created').length;
      const updated = r.items.filter((i) => i.status === 'updated').length;
      toast.success(
        `Imported ${added} skill${added === 1 ? '' : 's'}${updated ? `, updated ${updated}` : ''}`,
        { description: 'Attach them to agents on the agent’s Skills tab.' },
      );
      void queryClient.invalidateQueries({ queryKey: ['skills', tenantId] });
      setOpen(false);
    },
  });

  // Preview as soon as files are chosen, and again when the conflict choice changes.
  useEffect(() => {
    if (collected && collected.skills.length > 0) send.mutate({ dryRun: true });
    else if (collected) setResult({ dryRun: true, items: collected.local });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run for new files or conflict mode
  }, [collected, onConflict]);

  const load = async (files: PickedFile[]) => {
    setReading(true);
    setResult(null);
    send.reset();
    try {
      setCollected(await collect(files));
    } finally {
      setReading(false);
    }
  };

  const onDrop = async (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const entries = [...e.dataTransfer.items]
      .map((item) => item.webkitGetAsEntry?.())
      .filter((entry): entry is FileSystemEntry => Boolean(entry));
    const out: PickedFile[] = [];
    if (entries.length > 0) for (const entry of entries) await readEntry(entry, '', out);
    else for (const file of e.dataTransfer.files) out.push({ path: file.name, file });
    await load(out);
  };

  const items = result?.items ?? [];
  const toSave = items.filter((i) => i.status === 'create' || i.status === 'update').length;
  const found = (collected?.skills.length ?? 0) + (collected?.local.length ?? 0);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setCollected(null);
          setResult(null);
          send.reset();
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline">
          <FolderUp aria-hidden /> Import skills folder
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Import a skills folder</DialogTitle>
          <DialogDescription>
            Choose a folder such as <code>~/.claude/skills</code> or a project’s{' '}
            <code>.claude/skills</code>. Every <code>SKILL.md</code> (frontmatter <code>name</code>{' '}
            and <code>description</code>, then instructions) becomes a skill.
          </DialogDescription>
        </DialogHeader>
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => void onDrop(e)}
          className={cn(
            'grid place-items-center gap-2 rounded-lg border border-dashed p-6 text-center text-sm transition-colors',
            dragging && 'bg-muted border-foreground',
          )}
          data-testid="skills-drop-zone"
        >
          <FolderOpen className="text-muted-foreground size-6" aria-hidden />
          <p className="text-muted-foreground">Drop a skills folder (or SKILL.md files) here, or</p>
          <Button size="sm" onClick={() => inputRef.current?.click()} disabled={reading}>
            Choose folder
          </Button>
          <input
            ref={folderInput}
            type="file"
            multiple
            className="sr-only"
            aria-label="Skills folder"
            data-testid="skills-folder-input"
            onChange={(e) => {
              const files = [...(e.target.files ?? [])].map((file) => ({
                path: file.webkitRelativePath || file.name,
                file,
              }));
              e.target.value = '';
              void load(files);
            }}
          />
          {collected && (
            <p className="text-xs" aria-live="polite">
              {collected.folderName && (
                <>
                  <span className="font-mono">{collected.folderName}</span> ·{' '}
                </>
              )}
              {found === 0
                ? 'No SKILL.md files found'
                : `${found} skill${found === 1 ? '' : 's'} found`}
            </p>
          )}
        </div>

        {found > 0 && (
          <div className="grid gap-1.5 sm:w-64">
            <Label htmlFor="skills-conflict">When a skill already exists</Label>
            <Select value={onConflict} onValueChange={(v) => setOnConflict(v as 'skip' | 'update')}>
              <SelectTrigger id="skills-conflict" aria-label="When a skill already exists">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="skip">Skip it</SelectItem>
                <SelectItem value="update">Update it from the folder</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        <ErrorAlert error={send.error} />
        {(reading || (send.isPending && send.variables?.dryRun)) && (
          <p className="text-muted-foreground text-sm">Reading skills…</p>
        )}
        {items.length > 0 && (
          <ul className="grid gap-2" aria-label="Skills to import">
            {items.map((item) => (
              <li
                key={item.path}
                className="grid gap-1 rounded-md border p-2 text-xs"
                data-testid="skill-import-item"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-sm font-medium">{item.name}</span>
                  {item.size > 0 && (
                    <span className="text-muted-foreground">
                      {item.size.toLocaleString()} chars
                    </span>
                  )}
                  <Badge
                    variant={item.status === 'skip' ? 'outline' : 'secondary'}
                    className="ml-auto"
                  >
                    {STATUS_LABEL[item.status]}
                  </Badge>
                </div>
                {item.description && (
                  <p className="text-muted-foreground line-clamp-2">{item.description}</p>
                )}
                <p className="text-muted-foreground font-mono">{item.path}</p>
                {item.files.length > 0 && (
                  <p>
                    + {item.files.length} reference file{item.files.length === 1 ? '' : 's'}:{' '}
                    <span className="text-muted-foreground font-mono">
                      {item.files
                        .slice(0, 6)
                        .map((f) => f.path)
                        .join(', ')}
                      {item.files.length > 6 ? `, +${item.files.length - 6} more` : ''}
                    </span>
                  </p>
                )}
                {item.reason && (
                  <p className={item.status === 'skip' ? 'text-destructive' : ''}>{item.reason}</p>
                )}
                {item.warnings.map((w) => (
                  <p key={w} className="text-muted-foreground">
                    {w}
                  </p>
                ))}
              </li>
            ))}
          </ul>
        )}
        <DialogFooter>
          <Button
            disabled={toSave === 0 || send.isPending || reading}
            onClick={() => send.mutate({ dryRun: false })}
          >
            {send.isPending && !send.variables?.dryRun
              ? 'Importing…'
              : `Import${toSave ? ` ${toSave} skill${toSave === 1 ? '' : 's'}` : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
